import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FaExclamation, FaCalendarAlt } from 'react-icons/fa';
import { GiCancel } from 'react-icons/gi';
import { useStateContext } from '../../contexts/ContextProvider';
import axiosClient from '../../axios-client';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import MyLabel from '../../components/MyLabel';
import HourLoading from '../../components/HourLoading';

const EleveObservation = ({ student, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [observations, setObservations] = useState([]);
  const [errors, setErrors] = useState(null);
  const { token, setToken, user } = useStateContext();
  const navigate = useNavigate();

  const getObservations = async () => {
    setLoading(true);
    try {
      const { data } = await axiosClient.get(`/eleveObservations/${student.id}`);
      setObservations(data.observations);
      setErrors(null);
    } catch (err) {
      const response = err.response;
      if (response?.status === 401) {
        setToken(null);
      } else if (response?.status === 422) {
        setErrors(response.data.errors);
      } else if (response?.status === 403) {
        navigate('/403');
      } else if (response?.status === 404) {
        navigate('/404');
      } else {
        navigate('/500');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getObservations();
  }, [student.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 md:p-8 lg:p-10 animate-fade-in"
        role="dialog"
        aria-labelledby="student-observations-title"
      >
        {/* Header */}
        <Header
          icon={<FaExclamation className="text-yellow-600" />}
          title={student.eleve}
          titleAr={student.eleve}
          className="mb-4"
        />

        {/* Errors */}
        {errors && (
          <div className="mb-4 space-y-2">
            {Object.keys(errors).map((key) => (
              <Alert key={key} message={errors[key][0]} />
            ))}
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end mb-4">
          <Button
            icon={<GiCancel />}
            onClick={onClose}
            color="bg-red-500 hover:bg-red-600"
            textColor="text-white"
            text="Fermer"
            className="transition-colors duration-200"
          />
        </div>

        {/* Observations List */}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <HourLoading />
          </div>
        ) : (
          <div className="space-y-4">
            {observations.length === 0 ? (
              <p className="text-gray-500 text-center">Aucune observation trouvée.</p>
            ) : (
              observations.map((observation, index) => (
                <div
                  key={observation.id || index}
                  className={`flex items-center gap-4 p-4 rounded-lg shadow-md border-l-4 ${
                    observation.positive
                      ? 'border-l-green-500 bg-green-50'
                      : 'border-l-red-500 bg-red-50'
                  } hover:shadow-lg transition-all duration-200`}
                  role="region"
                  aria-label={`Observation ${observation.content}`}
                >
                  <FaExclamation className="text-3xl text-gray-700" />
                  <div className="flex-1 space-y-2">
                    <MyLabel
                      label="Auteur"
                      labelAr="من طرف"
                      content={observation.auteur}
                      cls="flex justify-between px-4 text-gray-700"
                    />
                    <MyLabel
                      label="Date"
                      labelAr="التاريخ"
                      content={observation.dateObservation}
                      cls="flex justify-between px-4 text-gray-700"
                    />
                    <MyLabel
                      content={user.langue === 'AR' ? observation.contentAr : observation.content}
                      cls="flex justify-between px-4 text-gray-700"
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// PropTypes for type checking
EleveObservation.propTypes = {
  student: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eleve: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

// Default props
EleveObservation.defaultProps = {
  student: {
    id: '',
    eleve: 'Unknown Student',
  },
};

export default EleveObservation;